import React from "react";
import { HOUSING_ORGS, HOUSING_MISTAKES, AGENCIES_WARNING, HOUSING_INSURANCE, INSURANCE_TIPS } from "@/data/studentData";
import LogoTile from "@/components/LogoTile";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AlertTriangle, Lightbulb, Sparkles, ShieldAlert, ExternalLink, Umbrella } from "lucide-react";

export default function Logement() {
  return (
    <div className="space-y-10" data-testid="logement-page">
      <header>
        <p className="text-xs uppercase tracking-[0.2em] text-indigo-500 font-semibold">Section Logement</p>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 mt-2">Trouver un logement étudiant</h1>
        <p className="text-slate-600 mt-3 max-w-2xl">
          Avant de visiter, prépare ton dossier. C'est LA clé pour décrocher un bien rapidement, surtout en zone tendue.
        </p>
      </header>

      {/* Highlight: Dossier Facile */}
      <section className="rounded-2xl border border-yellow-200 bg-yellow-50 p-6 sm:p-8" data-testid="dossierfacile-highlight">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-full bg-yellow-300 text-slate-900 flex items-center justify-center shrink-0">
            <Sparkles size={20} />
          </div>
          <div className="flex-1">
            <p className="text-xs uppercase tracking-wider font-semibold text-yellow-800">Commence ici</p>
            <h2 className="font-display text-2xl font-bold text-slate-900 mt-1">
              Mon Dossier Facile — le réflexe n°1
            </h2>
            <p className="text-slate-700 mt-2">
              Service public gratuit qui <strong>certifie</strong> tes pièces (pièce d'identité, avis d'imposition, RIB, garant).
              Les bailleurs le reconnaissent et font confiance. Tu constitues ton dossier <strong>une seule fois</strong>, tu l'envoies en PDF à toutes tes visites.
            </p>
            <ul className="mt-3 text-slate-700 text-sm space-y-1.5 list-disc list-inside">
              <li>Tu gagnes un temps fou sur chaque candidature</li>
              <li>Tu rassures le bailleur : pièces vérifiées par l'État</li>
              <li>Tu protèges tes données : pièces floutées automatiquement</li>
              <li>Indispensable avant de lancer tes recherches</li>
            </ul>
            <a
              href="https://www.dossierfacile.logement.gouv.fr"
              target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 mt-5 px-5 py-2.5 rounded-full bg-slate-900 text-white text-sm font-medium hover:bg-slate-800"
              data-testid="dossierfacile-link-main"
            >
              Créer mon DossierFacile <ExternalLink size={14} />
            </a>
          </div>
        </div>
      </section>

      <Tabs defaultValue="orgs" className="w-full">
        <TabsList className="bg-slate-100 flex-wrap h-auto">
          <TabsTrigger value="orgs" data-testid="tab-orgs">Organismes & plateformes</TabsTrigger>
          <TabsTrigger value="mistakes" data-testid="tab-mistakes">Erreurs à éviter</TabsTrigger>
          <TabsTrigger value="agencies" data-testid="tab-agencies">Agences : vigilance</TabsTrigger>
          <TabsTrigger value="insurance" data-testid="tab-insurance"><Umbrella size={14} className="mr-1.5" /> Assurance habitation</TabsTrigger>
        </TabsList>

        <TabsContent value="orgs" className="pt-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {HOUSING_ORGS.map((o) => <LogoTile key={o.id} item={o} />)}
          </div>
        </TabsContent>

        <TabsContent value="mistakes" className="pt-6 space-y-4">
          {HOUSING_MISTAKES.map((m, idx) => (
            <div key={idx} className="rounded-xl border border-slate-200 bg-white p-5 flex gap-4" data-testid={`mistake-${idx}`}>
              <div className="w-9 h-9 rounded-lg bg-red-50 text-red-500 flex items-center justify-center shrink-0">
                <AlertTriangle size={18} />
              </div>
              <div>
                <p className="font-display font-semibold text-slate-900">{m.title}</p>
                <p className="text-slate-600 text-sm mt-1">{m.detail}</p>
              </div>
            </div>
          ))}
          <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-5 flex gap-4">
            <div className="w-9 h-9 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center shrink-0">
              <Lightbulb size={18} />
            </div>
            <p className="text-slate-700 text-sm">
              <strong>Astuce :</strong> prépare un petit mail type de candidature avec ton DossierFacile en pièce jointe.
              Réponds dans les 30 minutes aux annonces — les bailleurs choisissent au premier arrivé solide.
            </p>
          </div>
        </TabsContent>

        <TabsContent value="agencies" className="pt-6 space-y-4">
          <div className="rounded-xl border border-red-200 bg-red-50 p-6">
            <div className="flex items-start gap-3">
              <ShieldAlert size={22} className="text-red-600 mt-1" />
              <div>
                <h3 className="font-display text-xl font-bold text-red-900">{AGENCIES_WARNING.title}</h3>
                <p className="text-red-900/80 mt-2">{AGENCIES_WARNING.body}</p>
              </div>
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-6">
            <p className="font-display font-semibold text-slate-900 mb-3">Signaux d'alerte (à fuir)</p>
            <ul className="space-y-2">
              {AGENCIES_WARNING.redFlags.map((rf, idx) => (
                <li key={idx} className="flex items-start gap-3 text-slate-700">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-red-500 shrink-0" />
                  <span className="text-sm">{rf}</span>
                </li>
              ))}
            </ul>
            <a
              href={AGENCIES_WARNING.report}
              target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 mt-5 px-4 py-2 rounded-full bg-slate-900 text-white text-sm hover:bg-slate-800"
              data-testid="report-link"
            >
              Signaler un abus (SignalConso) <ExternalLink size={14} />
            </a>
          </div>
        </TabsContent>

        <TabsContent value="insurance" className="pt-6 space-y-6">
          <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-5 text-slate-700 text-sm">
            <strong>Obligatoire avant la remise des clés.</strong> L'assurance habitation couvre les dégâts causés au logement et à toi-même. Compare au moins 3 devis, lis la franchise et garde ton attestation à portée de main pour la signature du bail.
          </div>
          <h3 className="font-display text-xl font-semibold text-slate-800">Assureurs étudiants recommandés</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {HOUSING_INSURANCE.map((i) => <LogoTile key={i.id} item={i} />)}
          </div>
          <h3 className="font-display text-xl font-semibold text-slate-800 pt-2">Astuces avant de signer</h3>
          <div className="space-y-3">
            {INSURANCE_TIPS.map((t, idx) => (
              <div key={idx} className="rounded-xl border border-slate-200 bg-white p-4 flex gap-3 items-start" data-testid={`insurance-tip-${idx}`}>
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-indigo-500 shrink-0" />
                <p className="text-slate-700 text-sm">{t}</p>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
